// frontend/src/pages/ForgotPassword.tsx
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next"; 
import { requestPasswordReset } from "../services/auth"; 
import { showToast } from "../utils/toast";
import Spinner from "../components/ui/Spinner";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const widgetElRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | number | null>(null);
  const tokenRef = useRef<string | null>(null);

  const { t } = useTranslation();

  useEffect(() => {
    const sitekey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;
    if (!sitekey) {
      console.error("[ForgotPassword] Missing VITE_TURNSTILE_SITE_KEY env.");
      return;
    }

    const tryRender = () => {
      if (!window.turnstile?.render) return false;
      const el = widgetElRef.current;
      if (!el) return false;

      // Visible widget. Valid sizes: 'normal' | 'flexible' | 'compact'
      widgetIdRef.current = window.turnstile.render(el, {
        sitekey,
        size: "normal",
        appearance: "always",        // show from the beginning
        // Called when a token is issued (also after challenge passes)
        callback: (token: string) => {
          tokenRef.current = token;
        },
        // Clear token on error/timeout/expire; user may need to retry
        "error-callback": () => { tokenRef.current = null; },
        "timeout-callback": () => { tokenRef.current = null; },
        "expired-callback": () => { tokenRef.current = null; },
      });

      return true;
    };

    // Render once when script is available
    const id = window.setInterval(() => {
      if (tryRender()) {
        setReady(true);
        window.clearInterval(id);
      }
    }, 150);

    return () => window.clearInterval(id);
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Prefer freshest token if API is available
      const wid = widgetIdRef.current!;
      const latest =
        (window.turnstile?.getResponse?.(wid) as string | null | undefined) ?? tokenRef.current;

      if (!latest) {
        showToast(t("auth.forgot_password.verification"), "error");
        return;
      }

      await requestPasswordReset(email, latest);
      // Success: switch to confirmation state (no toast)
      setSubmitted(true);
    } catch (err: any) {
      console.error("[ForgotPassword] Turnstile/reset error:", err);
      showToast(t("auth.forgot_password.error"), "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-full bg-gray-50 px-4 pt-12 md:pt-16 lg:pt-0 lg:py-8 lg:flex-1 lg:grid lg:place-items-center">
      <div className="w-full max-w-md bg-white shadow-md rounded px-8 py-6">
        <h1 className="mx-auto text-lg font-semibold mb-4">{t("auth.forgot_password.title")}</h1>

        {submitted ? (
          <>
            <p className="text-sm text-gray-700">
              {t("auth.forgot_password.submitted")}
            </p>
            <div className="mt-4 text-sm">
              <Link
                to="/login"
                className="inline-flex items-center justify-center rounded px-4 py-2 bg-elani-blue text-white hover:opacity-90"
              >
                {t("auth.forgot_password.back_to_login")}
              </Link>
            </div>
          </>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">{t("common.email")}</label>
              <input
                type="email"
                className="standard-form-field"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>

            <button
              type="submit"
              className="w-full bg-elani-blue text-white py-2 rounded disabled:opacity-60"
              disabled={loading || !ready}
              title={!ready ? t("auth.forgot_password.preparing") : undefined}
            >
              {loading ? (
                <>
                  <Spinner size="sm" className="mr-2" aria-label={t("auth.forgot_password.a11y_sending_label")} />
                  {t("auth.forgot_password.sending")}
                </>
              ) : (
                t("auth.forgot_password.submit")
              )}
            </button>

            {/* Visible Turnstile widget container */}
            <div className="mt-2" ref={widgetElRef} />

            <div className="mt-4 text-sm">
              <a href="/login" className="text-elani-orange hover:underline">{t("auth.forgot_password.back_to_login")}</a>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}